// Progress (spec §6: weekly sets per muscle vs target, e1RM per main lift)
// as rules: a muscle's week, readiness and last training in words, the
// regions it's grouped by, its bar's geometry, and each main lift's line in
// a sentence. Pure — pinned by progress.test.js; the components lay these
// out.
import { MUSCLES, REGIONS, dayKey, formatNumber, muscleRegion, regionLabel, setLine } from "./format";
import { dayNumber } from "./chart";

const has = (value) => value !== null && value !== undefined && value !== "";

// --- readiness, in the engine's own thresholds -------------------------------

// backend/training/engine_constants.py: LOW_READINESS — a template with a
// primary muscle below it scores lower ("still recovering", readiness.py
// _template_reason); SECONDARY_READY_MIN — below it, the plan adds no
// accessory that works the muscle on the side (fill.py). Keep in step.
export const LOW_READINESS = 0.6;
export const SECONDARY_READY_MIN = 0.5;

/**
 * Readiness (0–1, from summary/) in words: "Recovered", "Recovering"
 * (below LOW_READINESS) or "Tired" (below SECONDARY_READY_MIN), with the
 * percentage beside it when it isn't 100% — never a bare percentage. The
 * percentage rounds down, so it never contradicts the word ("Recovering
 * (59%)", not "(60%)", at 0.599). Unknown → null.
 */
export function readinessWords(readiness) {
  if (!Number.isFinite(readiness)) return null;
  const pct = Math.floor(Math.min(Math.max(readiness, 0), 1) * 100 + 1e-9);
  const word = readiness < SECONDARY_READY_MIN ? "Tired" : readiness < LOW_READINESS ? "Recovering" : "Recovered";
  return pct >= 100 ? word : `${word} (${pct}%)`;
}

// --- calendar days in the person's zone (spec §5.8, W9) -------------------------

/**
 * Whole calendar days from `iso` to `now` in `timeZone` (today 0, yesterday
 * 1) — the engine's rule for every "N days since", not elapsed hours.
 * Null when `iso` isn't a time. A time in the future counts as today.
 */
export function calendarDaysSince(iso, timeZone, now = new Date()) {
  const then = dayNumber(dayKey(iso, timeZone));
  const today = dayNumber(dayKey(now, timeZone));
  if (Number.isNaN(then) || Number.isNaN(today)) return null;
  return Math.max(0, today - then);
}

const WEEKDAY = { weekday: "long" };

/** A day in `timeZone` said with `options` (toLocaleDateString's), or "". */
function sayDay(iso, timeZone, options) {
  const key = dayKey(iso, timeZone);
  const n = dayNumber(key);
  if (Number.isNaN(n)) return "";
  // the calendar day itself, said at noon UTC: no zone can move it
  return new Date(`${key}T12:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC", ...options });
}

/**
 * When a muscle was last trained, in calendar days where the person lives:
 * "Last trained today", "yesterday", "Monday (3 days ago)" within the week,
 * then "Sep 14 (24 days ago)" — the year too when it isn't this year
 * there. Never trained (null): "Not trained yet".
 */
export function lastTrainedWords(iso, timeZone, now = new Date()) {
  if (!has(iso)) return "Not trained yet";
  const days = calendarDaysSince(iso, timeZone, now);
  if (days === null) return "Not trained yet";
  if (days === 0) return "Last trained today";
  if (days === 1) return "Last trained yesterday";
  if (days < 7) return `Last trained ${sayDay(iso, timeZone, WEEKDAY)} (${days} days ago)`;
  const sameYear = dayKey(iso, timeZone).slice(0, 4) === dayKey(now, timeZone).slice(0, 4);
  const day = sayDay(iso, timeZone, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
  return `Last trained ${day} (${days} days ago)`;
}

// --- this week, per muscle -------------------------------------------------------

/**
 * A muscle's week in words, after its label: "6 of 9 sets this week",
 * "12 of 9 sets this week — over the target", "1 set this week, no weekly
 * target", and "no sets this week and no weekly target" for one that's
 * quiet. Fractional sets (D6) as they are: "6.5 of 9".
 */
export function weekWords(volume, target) {
  const v = Number(volume) || 0;
  const t = Number(target) || 0;
  if (t <= 0) {
    if (v <= 0) return "no sets this week and no weekly target";
    return `${formatNumber(v)} ${v === 1 ? "set" : "sets"} this week, no weekly target`;
  }
  const line = `${formatNumber(v)} of ${formatNumber(t)} sets this week`;
  return v > t ? `${line} — over the target` : line;
}

/** A quiet muscle: no weekly target and nothing done this week. */
export const isQuiet = (muscle) => !(Number(muscle?.target) > 0) && !(Number(muscle?.volume) > 0);

// Room past the target, so a bar at the target doesn't fill its track and
// one over it still shows how far over.
const HEADROOM = 1.25;

/**
 * The bar's geometry, in % of its track: `{fill, tick}` — the sets done,
 * and the target's tick. The track runs to 125% of the target, or to the
 * volume when that's further. Null with no weekly target (no bar at all):
 * with nothing to measure against, any fill would be made up — a full one
 * reads as done, like a muscle far over its target — so the text ("2 sets
 * this week, no weekly target") carries the row.
 */
export function barGeometry(volume, target) {
  const v = Math.max(Number(volume) || 0, 0);
  const t = Number(target) || 0;
  if (t <= 0) return null;
  const full = Math.max(t * HEADROOM, v);
  const pct = (value) => Math.round((value / full) * 1000) / 10;
  return { fill: pct(v), tick: pct(t) };
}

/**
 * summary/'s muscles grouped by region, in the check-in's region order
 * (backend/training/muscles.py REGIONS) and the catalog's muscle order
 * within each: `[{region, label, muscles}]`. A code the catalog doesn't
 * know (a newer backend) is kept, in an "Other" group at the end.
 */
export function muscleGroups(muscles) {
  const order = new Map(MUSCLES.map((m, i) => [m.code, i]));
  const sorted = [...(muscles ?? [])].sort(
    (a, b) => (order.get(a.muscle) ?? Infinity) - (order.get(b.muscle) ?? Infinity),
  );
  const groups = REGIONS.map((region) => ({ region: region.code, label: regionLabel(region.code), muscles: [] }));
  const other = { region: "other", label: "Other", muscles: [] };
  const byRegion = new Map(groups.map((group) => [group.region, group]));
  for (const muscle of sorted) {
    (byRegion.get(muscleRegion(muscle.muscle)) ?? other).muscles.push(muscle);
  }
  return [...groups, other].filter((group) => group.muscles.length > 0);
}

// --- estimated max, per main lift -------------------------------------------------

// backend/training/engine_constants.py VERTICAL: the vertical pull the
// settings choose. summary/ tracks both; the other one is shown only once
// it has points.
const VERTICAL = { chin_up: "chin-up", lat_pulldown: "lat-pulldown" };

/**
 * The main lifts Progress charts: summary/'s, in its order, without the
 * vertical pull the settings didn't choose — unless it has points (it was
 * done, and its history is worth showing).
 */
export function chartedLifts(mainLifts, verticalPull) {
  const chosen = VERTICAL[verticalPull];
  const others = new Set(Object.values(VERTICAL).filter((key) => key !== chosen));
  return (mainLifts ?? []).filter(
    (lift) => !(chosen && others.has(lift.exercise) && !(lift.trend?.length > 0)),
  );
}

/** "~176": an estimate, rounded to the pound or kilo. */
export const approx = (value) => `~${formatNumber(Math.round(Number(value)))}`;

/**
 * A trend day ("YYYY-MM-DD", already the person's) said "Aug 3" (`short`)
 * or "Mon, Aug 3"; the year too when it isn't `todayKey`'s.
 */
export function trendDay(day, todayKey, { short = false } = {}) {
  if (Number.isNaN(dayNumber(day))) return "";
  const sameYear = String(todayKey ?? "").slice(0, 4) === day.slice(0, 4);
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, {
    timeZone: "UTC",
    ...(short ? {} : { weekday: "short" }),
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/**
 * The trend's best point: the one at summary/'s `best` — the latest of
 * equals, so a best matched again is marked where it was matched. Null
 * with no points.
 */
export function bestPoint(lift) {
  const trend = lift?.trend ?? [];
  if (trend.length === 0) return null;
  const top = Number.isFinite(lift.best) ? lift.best : Math.max(...trend.map((p) => p.e1rm));
  for (let i = trend.length - 1; i >= 0; i -= 1) if (trend[i].e1rm >= top) return trend[i];
  return trend.at(-1);
}

/**
 * One lift's line in words — the text the chart is a picture of:
 * - none: what will appear, and after what;
 * - one: "One session so far: ~180 lb from 135 lb × 8 @ 2 in reserve, on
 *   Mon, Aug 3.";
 * - more: "~176 → ~189 lb since Aug 3, over 7 sessions." and the best.
 * A bodyweight lift's numbers are effective loads (bodyweight + added −
 * band, D17), said as the session summary says them: "~196 lb including
 * bodyweight" (W16).
 */
export function liftWords(lift, { units = "lb", exercise, todayKey } = {}) {
  const trend = lift?.trend ?? [];
  const withBody = exercise?.load_kind === "bodyweight";
  const name = String(lift?.name ?? lift?.exercise ?? "this lift");
  const lower = name.slice(0, 2) === name.slice(0, 2).toUpperCase() ? name : name.charAt(0).toLowerCase() + name.slice(1);
  if (trend.length === 0) {
    return [
      `No estimate yet. After a session where you log ${lower} with the reps you had in reserve, its best set shows here — one point per session.`,
    ];
  }
  const body = withBody ? " including bodyweight" : "";
  const latest = trend.at(-1);
  if (trend.length === 1) {
    const from = latest.set ? ` from ${setLine(latest.set, units, exercise)}` : "";
    return [
      `One session so far: ${approx(latest.e1rm)} ${units}${body}${withBody && from ? "," : ""}${from}, on ${trendDay(latest.date, todayKey)}.`,
      "A line appears after the next one.",
    ];
  }
  const first = trend[0];
  const sessions = `over ${trend.length} sessions`;
  const since = trendDay(first.date, todayKey, { short: true });
  const change =
    Math.round(first.e1rm) === Math.round(latest.e1rm)
      ? `Holding at ${approx(latest.e1rm)} ${units}${body} since ${since}, ${sessions}.`
      : `${approx(first.e1rm)} → ${approx(latest.e1rm)} ${units}${body} since ${since}, ${sessions}.`;
  const best = bestPoint(lift);
  const bestLine =
    best === latest
      ? "The latest is the best."
      : `Best: ${approx(best.e1rm)} ${units}${body}, on ${trendDay(best.date, todayKey)}.`;
  return [change, bestLine];
}
